import type { Pos } from '@games/core'
import { COLS, ROWS } from './types'
import type { Board, Color, FogChessState, Move, PieceType } from './types'
import { applyMove, createInitialBoard, genMoves } from './rules'

/** 深拷贝棋盘，避免调用方共享可变引用。 */
export function cloneBoard(board: Board): Board {
  return board.map((row) => row.map((piece) => (piece ? { ...piece } : null)))
}

/** 初始状态：红先行，无胜者，无吃子记录。 */
export function createInitialState(): FogChessState {
  return {
    board: createInitialBoard(),
    turn: 'r',
    winner: null,
    moveCount: 0,
    lastMove: null,
    captured: { r: [], b: [] },
  }
}

function other(color: Color): Color {
  return color === 'r' ? 'b' : 'r'
}

function inBounds(pos: Pos): boolean {
  return pos.r >= 0 && pos.r < ROWS && pos.c >= 0 && pos.c < COLS
}

/**
 * 校验轮次 + 归属 + 合法性。
 * 坐标越界、目标非法（含炮架等 visionOnly 格）、非当前回合、非本方棋子都返回 false。
 */
export function isLegalMove(state: FogChessState, move: Move, playerId: Color): boolean {
  if (state.winner !== null) return false
  if (state.turn !== playerId) return false
  if (!inBounds(move.from) || !inBounds(move.to)) return false
  const piece = state.board[move.from.r][move.from.c]
  if (!piece || piece.color !== playerId) return false
  return genMoves(state.board, move.from.r, move.from.c).some(
    (target) => !target.visionOnly && target.r === move.to.r && target.c === move.to.c,
  )
}

/** 纯函数：返回新 state，更新 board / turn / lastMove / moveCount / captured / winner。 */
export function applyMoveToState(state: FogChessState, move: Move): FogChessState {
  if (state.winner !== null) return state
  const mover = state.board[move.from.r][move.from.c]
  if (!mover) return state

  const { board, captured } = applyMove(state.board, move.from, move.to)

  const capturedRecord: Record<Color, readonly PieceType[]> = captured
    ? captured.color === 'r'
      ? { r: [...state.captured.r, captured.type], b: [...state.captured.b] }
      : { r: [...state.captured.r], b: [...state.captured.b, captured.type] }
    : { r: [...state.captured.r], b: [...state.captured.b] }

  // 该变体没有将军/将死判定：吃掉对方 K 即由走子方获胜
  const kingCaptured = captured !== null && captured.type === 'K'

  return {
    board,
    turn: kingCaptured ? state.turn : other(mover.color),
    winner: kingCaptured ? mover.color : null,
    moveCount: state.moveCount + 1,
    lastMove: { from: { r: move.from.r, c: move.from.c }, to: { r: move.to.r, c: move.to.c } },
    captured: capturedRecord,
  }
}